import { useQuery } from "@tanstack/react-query";
import { endOfDay, format, startOfDay, subDays } from "date-fns";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { normalizePaymentMethod } from "@/components/growth/utils";

export function useFinancialHistory(activeCompanyId: string | null) {
  const [period, setPeriod] = useState<"7d" | "30d" | "custom">("7d");
  const [customStart, setCustomStart] = useState(
    format(subDays(new Date(), 7), "yyyy-MM-dd"),
  );
  const [customEnd, setCustomEnd] = useState(format(new Date(), "yyyy-MM-dd"));

  const dateFilter = useMemo(() => {
    const today = new Date();
    if (period === "7d") {
      return {
        start: startOfDay(subDays(today, 7)).toISOString(),
        end: endOfDay(today).toISOString(),
      };
    } else if (period === "30d") {
      return {
        start: startOfDay(subDays(today, 30)).toISOString(),
        end: endOfDay(today).toISOString(),
      };
    } else {
      const [sYear, sMonth, sDay] = (
        customStart || format(subDays(today, 7), "yyyy-MM-dd")
      )
        .split("-")
        .map(Number);
      const [eYear, eMonth, eDay] = (customEnd || format(today, "yyyy-MM-dd"))
        .split("-")
        .map(Number);

      const startDate = new Date(sYear, sMonth - 1, sDay, 0, 0, 0, 0);
      const endDate = new Date(eYear, eMonth - 1, eDay, 23, 59, 59, 999);

      return {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      };
    }
  }, [period, customStart, customEnd]);

  // 1. Fetch cash_transactions
  const cashTxQuery = useQuery({
    queryKey: [
      "financial_history_cash_tx",
      activeCompanyId,
      dateFilter.start,
      dateFilter.end,
    ],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("cash_transactions")
        .select("id, type, amount, method, description, created_at")
        .eq("company_id", activeCompanyId)
        .gte("created_at", dateFilter.start)
        .lte("created_at", dateFilter.end);

      if (error) throw error;
      return data || [];
    },
    enabled: !!activeCompanyId,
  });

  // 2. Fetch completed/paid orders
  const ordersQuery = useQuery({
    queryKey: [
      "financial_history_orders",
      activeCompanyId,
      dateFilter.start,
      dateFilter.end,
    ],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, total, services, payment_method, status, created_at, paid_at, completed_at",
        )
        .eq("company_id", activeCompanyId)
        .in("status", ["CONCLUIDO", "PAGO"])
        .gte("created_at", dateFilter.start)
        .lte("created_at", dateFilter.end);

      if (error) throw error;
      return data || [];
    },
    enabled: !!activeCompanyId,
  });

  // 3. Fetch collaborator commissions
  const commissionsQuery = useQuery({
    queryKey: [
      "financial_history_commissions",
      activeCompanyId,
      dateFilter.start,
      dateFilter.end,
    ],
    queryFn: async () => {
      if (!activeCompanyId) return 0;
      let total = 0;

      try {
        const { data, error } = await supabase
          .from("collaborator_commissions")
          .select("amount")
          .eq("company_id", activeCompanyId)
          .gte("created_at", dateFilter.start)
          .lte("created_at", dateFilter.end);

        if (!error && data && data.length > 0) {
          total = data.reduce(
            (sum: number, c: any) => sum + (Number(c.amount) || 0),
            0,
          );
        }
      } catch {
        // Continue to ledger fallback
      }

      if (total === 0) {
        try {
          const { data: ledgerData } = await supabase
            .from("commissions_ledger")
            .select("amount")
            .eq("company_id", activeCompanyId)
            .gte("created_at", dateFilter.start)
            .lte("created_at", dateFilter.end);

          if (ledgerData && ledgerData.length > 0) {
            total = ledgerData.reduce(
              (sum: number, c: any) => sum + (Number(c.amount) || 0),
              0,
            );
          }
        } catch {
          // Ignore
        }
      }

      return total;
    },
    enabled: !!activeCompanyId,
  });

  const cashTxData = cashTxQuery.data || [];
  const ordersData = ordersQuery.data || [];
  const commissionsTotal = commissionsQuery.data || 0;
  const isLoading =
    cashTxQuery.isLoading ||
    ordersQuery.isLoading ||
    commissionsQuery.isLoading;

  // Calculate aggregated financial metrics
  const metrics = useMemo(() => {
    let txReceitas = 0;
    let despesas = 0;
    const paymentMethodsMap: Record<string, number> = {};

    cashTxData.forEach((tx: any) => {
      const amount = Number(tx.amount) || 0;
      if (tx.type === "ENTRADA") {
        txReceitas += amount;
        if (tx.method) {
          const normMethod = normalizePaymentMethod(tx.method);
          paymentMethodsMap[normMethod] =
            (paymentMethodsMap[normMethod] || 0) + amount;
        }
      } else if (tx.type === "SAIDA") {
        despesas += amount;
      }
    });

    const completedOrdersCount = ordersData.length;
    const ordersReceitas = ordersData.reduce(
      (sum: number, o: any) => sum + (Number(o.total) || 0),
      0,
    );

    const receitas = Math.max(txReceitas, ordersReceitas);
    const lucro = receitas - despesas;
    const lucroMargin = receitas > 0 ? (lucro / receitas) * 100 : 0;
    const ticketMedio =
      completedOrdersCount > 0
        ? (ordersReceitas > 0 ? ordersReceitas : receitas) /
          completedOrdersCount
        : 0;

    if (Object.keys(paymentMethodsMap).length === 0 && ordersData.length > 0) {
      ordersData.forEach((o: any) => {
        const amount = Number(o.total) || 0;
        const normMethod = normalizePaymentMethod(
          o.payment_method || "Dinheiro",
        );
        paymentMethodsMap[normMethod] =
          (paymentMethodsMap[normMethod] || 0) + amount;
      });
    }

    const defaultMethods = [
      "PIX",
      "Cartão de Crédito",
      "Cartão de Débito",
      "Dinheiro",
    ];
    const totalPaymentAmount =
      Object.values(paymentMethodsMap).reduce((a, b) => a + b, 0) ||
      receitas ||
      1;

    const paymentMethodsList = defaultMethods.map((methodName) => {
      const amount = paymentMethodsMap[methodName] || 0;
      const percent =
        totalPaymentAmount > 0 ? (amount / totalPaymentAmount) * 100 : 0;
      return { name: methodName, amount, percent };
    });

    Object.keys(paymentMethodsMap).forEach((methodName) => {
      if (!defaultMethods.includes(methodName)) {
        const amount = paymentMethodsMap[methodName];
        const percent =
          totalPaymentAmount > 0 ? (amount / totalPaymentAmount) * 100 : 0;
        paymentMethodsList.push({ name: methodName, amount, percent });
      }
    });

    const servicesMap: Record<
      string,
      { name: string; count: number; totalRevenue: number }
    > = {};

    ordersData.forEach((order: any) => {
      let orderServices = order.services;
      if (typeof orderServices === "string") {
        try {
          orderServices = JSON.parse(orderServices);
        } catch {
          orderServices = [orderServices];
        }
      }
      if (!Array.isArray(orderServices)) {
        orderServices = orderServices ? [orderServices] : [];
      }

      const orderTotal = Number(order.total) || 0;
      const countServices = orderServices.length || 1;

      orderServices.forEach((s: any) => {
        let sName = "";
        let sPrice = 0;

        if (typeof s === "string") {
          sName = s.trim();
          sPrice = orderTotal / countServices;
        } else if (typeof s === "object" && s !== null) {
          sName = s.name || s.title || s.service_name || "Serviço";
          sPrice = Number(s.price) || orderTotal / countServices;
        }

        if (sName) {
          if (!servicesMap[sName]) {
            servicesMap[sName] = { name: sName, count: 0, totalRevenue: 0 };
          }
          servicesMap[sName].count += 1;
          servicesMap[sName].totalRevenue += sPrice;
        }
      });
    });

    const topServices = Object.values(servicesMap)
      .sort((a, b) => b.totalRevenue - a.totalRevenue || b.count - a.count)
      .slice(0, 5);

    const topServiceMaxRevenue =
      topServices.length > 0
        ? Math.max(...topServices.map((s) => s.totalRevenue))
        : 1;

    const commissionPercent =
      receitas > 0 ? (commissionsTotal / receitas) * 100 : 0;

    return {
      receitas,
      despesas,
      lucro,
      lucroMargin,
      ticketMedio,
      completedOrdersCount,
      paymentMethodsList,
      topServices,
      topServiceMaxRevenue,
      commissionsTotal,
      commissionPercent,
    };
  }, [cashTxData, ordersData, commissionsTotal]);

  return {
    period,
    setPeriod,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    metrics,
    isLoading,
  };
}
